// Utility functions for atomic swap operations

/**
 * Generate a random secret for atomic swap
 * @returns Uint8Array secret (32 bytes)
 */
export function generateSecret(): Uint8Array {
    const secret = new Uint8Array(32);
    crypto.getRandomValues(secret);
    return secret;
}

/**
 * Hash a secret using SHA-256
 * @param secret - The secret to hash
 * @returns Promise<Uint8Array> - The hash of the secret
 */
export async function hashSecret(secret: Uint8Array): Promise<Uint8Array> {
    const hashBuffer = await crypto.subtle.digest('SHA-256', secret as BufferSource);
    return new Uint8Array(hashBuffer);
}

/**
 * Convert Uint8Array to hex string for display
 * @param bytes - Uint8Array to convert
 * @returns Hex string representation
 */
export function bytesToHex(bytes: Uint8Array): string {
    return Array.from(bytes)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
}

/**
 * Convert hex string to Uint8Array
 * @param hex - Hex string to convert
 * @returns Uint8Array
 */
export function hexToBytes(hex: string): Uint8Array {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
        bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes;
}

/**
 * Calculate time lock timestamp (current time + hours)
 * @param hours - Number of hours from now
 * @returns bigint timestamp in nanoseconds
 */
export function calculateTimeLock(hours: number): bigint {
    const now = Date.now();
    const futureTime = now + (hours * 60 * 60 * 1000);
    // Convert to nanoseconds for IC
    return BigInt(futureTime) * BigInt(1_000_000);
}

/**
 * Format timestamp for display
 * @param timestamp - Timestamp in nanoseconds
 * @returns Formatted date string
 */
export function formatTimestamp(timestamp: bigint): string {
    const milliseconds = Number(timestamp / BigInt(1_000_000));
    return new Date(milliseconds).toLocaleString();
}

/**
 * Check if time lock has expired
 * @param timeLock - Time lock timestamp in nanoseconds
 * @returns boolean
 */
export function isTimeLockExpired(timeLock: bigint): boolean {
    const now = BigInt(Date.now()) * BigInt(1_000_000);
    return now >= timeLock;
}

/**
 * Get time remaining until time lock expires
 * @param timeLock - Time lock timestamp in nanoseconds
 * @returns string representation of time remaining
 */
export function getTimeRemaining(timeLock: bigint): string {
    const now = BigInt(Date.now()) * BigInt(1_000_000);
    if (now >= timeLock) {
        return 'Expired';
    }
    
    const remainingMs = Number((timeLock - now) / BigInt(1_000_000));
    const hours = Math.floor(remainingMs / (1000 * 60 * 60));
    const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
}

/**
 * Check if blockchain supports atomic swaps (UTXO-based)
 * @param tokenSymbol - Token symbol to check
 * @returns boolean
 */
export function isAtomicSwapCompatible(tokenSymbol: string): boolean {
    // MM2 supports these UTXO-based chains for atomic swaps
    const utxoBlockchains = ['RVN', 'BTC', 'LTC', 'DOGE', 'BCH', 'ZEC', 'DASH', 'KMD', 'QTUM', 'KAS'];
    return utxoBlockchains.includes(tokenSymbol.toUpperCase());
}

/**
 * Check if token has a wrapped version available (EVM or native wrapped)
 * Separate from atomic swap compatibility - XRP and SOL have wrapped versions but aren't UTXO
 * @param tokenSymbol - Token symbol to check
 * @returns boolean
 */
export function hasWrappedVersion(tokenSymbol: string): boolean {
    // Tokens that have wrapped versions (WBTC only - other wrapped tokens removed)
    const wrappableTokens = ['BTC'];
    return wrappableTokens.includes(tokenSymbol.toUpperCase());
}

/**
 * Validate secret format
 * @param secret - Secret to validate
 * @returns boolean
 */
export function isValidSecret(secret: Uint8Array): boolean {
    return secret.length === 32;
}

/**
 * Validate hash format
 * @param hash - Hash to validate
 * @returns boolean
 */
export function isValidHash(hash: Uint8Array): boolean {
    return hash.length === 32;
}
